import { PreviousGameSuggestion } from './PreviousGameSuggestion';
import { isTieredVariant } from '@lub0v/santa-utils';
import { premiumFeatureAction } from '@src/pages/PremiumPage/featureTier';
import { getThemeOrder } from '@src/utils/themeOrder';
import { readFirstHomeTheme, readLastHomeTheme, trackCreationFirstThumbnail } from '@src/utils/themeAnalytics';
import { PremiumOfferView } from '@src/components/PremiumUpsell/PremiumOfferView';
import './GuestProfileChoice.css';
import { saveDeferredTheme, clearDeferredTheme, completeDeferredTheme } from './deferredTheme';
import { usePremiumOffer } from '@src/pages/PremiumPage/PremiumOfferProvider';
import { useStartPremiumPurchase } from '@src/components/PremiumUpsell/premiumInterest';
import { CrownIcon } from '@src/components/icons/CrownIcon';
import { closeDialog, showDialog } from '@src/components/Dialog/Dialog';
import { clearNewBoxDraft, newBoxDraftKey, readNewBoxDraft, saveNewBoxDraft, reloadedNewBoxDraftKey } from './savedDraft';
import { hasPremiumFeature } from '@lub0v/santa-utils';
import { NewBoxReview } from './NewBoxReview';
import { isDotCom } from '@src/site';
import { trackThemeIconSelected } from '@src/utils/themeAnalytics';
import { PREMIUM_THEMES } from '@lub0v/santa-utils';
import { AppearancePicker } from './AppearancePicker';
import { InlineAds } from '@src/components/Ads/Ads';
import { useTheme } from '@src/theme/useTheme';
import { isSeason } from '@src/theme/season';
import { NewBoxEmailStep } from './NewBoxEmailStep';
import { trackUmamiEvent } from '@src/utils/umami';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  isPlaceholderOrganizerUsername,
  normalizeQuickBoxName,
  type CashCurrency,
  type CustomField,
} from '@lub0v/santa-utils';
import {
  Button,
  Choice,
  ChoiceIcon,
  ChoicePanel,
  ClockIcon,
  Cluster,
  CreationShell,
  FileUpload,
  IMAGE_UPLOAD_MAX_SIZE,
  HintError,
  Input,
  NamesInput,
  Stack,
  PackageIcon,
  PicturePicker,
  Select,
  Typography,
  UsersIcon,
  type ChoiceIconTone,
  type FileUploadHandle,
  type NamesProblem,
} from '@src/components';
import { NamePresetPicker } from '@src/pages/NewBoxPage/NamePresetPicker';
import { BOX_NAME_MAX_LENGTH } from '@src/pages/NewBoxPage/namePresets';
import { storeOrganizerAccess } from '@src/access/organizerAccess';
import {
  emptyDraft,
  newBoxStepCopyKey,
  nextStep,
  prevStep,
  toCreateInput,
  visibleNewBoxSteps,
  type BudgetPreset,
  type ExchangeMethod,
  type NewBoxDraft,
  type NewBoxStep,
} from '@src/pages/NewBoxPage/draft';

import { boxOrganizerPath, boxPath } from '@src/routing/routes';
import {
  cashCurrencyCodes,
  currencyOptionLabel,
  defaultCashCurrency,
  getBudgetPresetAmounts,
  getCurrencySymbol,
} from '@src/site/currency';
import { santaClient } from '@src/state/client';
import { useAppDispatch, useAppSelector } from '@src/state/hooks';
import { loadSession } from '@src/state/sessionSlice';

import { useMatchesMedia, MOBILE_HEADER_MQ } from '@src/components/layout/useMobileHeaderRecede';

const BUDGET_PRESETS: BudgetPreset[] = ['none', 'low', 'mid', 'custom'];

const EXCHANGE_CHOICE_ICONS: Record<
  ExchangeMethod,
  { tone: ChoiceIconTone; Icon: typeof UsersIcon }
> = {
  meet: { tone: 'accent', Icon: UsersIcon },
  send: { tone: 'support', Icon: PackageIcon },
  pickup: { tone: 'support', Icon: PackageIcon },
  later: { tone: 'warning', Icon: ClockIcon },
};

function formatMoney(value: number) {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function extrasWithoutOrganizer(names: string[], organizerName: string): string[] {
  const organizer = normalizeQuickBoxName(organizerName);
  if (!organizer) {
    return names;
  }
  return names.filter((name) => normalizeQuickBoxName(name) !== organizer);
}

export function NewBoxPage() {
  const location = useLocation();
  const { i18n } = useTranslation();
  // Restore only after we know which browser profile owns the draft.
  const status = useAppSelector((state) => state.session.status);
  const [ready, setReady] = useState(status === 'ready');
  if (!ready && (status === 'ready' || status === 'failed')) setReady(true);
  if (!ready) return null;
  return <PreviousGameSuggestion key={`${location.key}:${i18n.resolvedLanguage}`}><NewBoxFlow /></PreviousGameSuggestion>;
}

function NewBoxFlow() {
  const [homeThemeAtCreation] = useState(readLastHomeTheme);
  const [firstHomeThemeAtCreation] = useState(readFirstHomeTheme);
  const startPremiumPurchase = useStartPremiumPurchase();
  const navigate = useNavigate();
  const mobile = useMatchesMedia(MOBILE_HEADER_MQ);
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.session.user);
  const [themeOrder] = useState(() => getThemeOrder(user?.id));
  const canUsePremiumThemes = hasPremiumFeature(user ?? {}, 'boxThemes');
  const [search] = useSearchParams();
  const year = new Date().getFullYear();
  const { seasonPackage, setSeason, theme: colorTheme } = useTheme();
  const premiumOffer = usePremiumOffer('themes');
  const defaultName = seasonPackage.id === 'halloween' ? 'halloween' : 'year';
  const resumeKey = search.get('resume');
  const entry = useLocation();
  const [restored] = useState(() => {
    const key = resumeKey ?? reloadedNewBoxDraftKey(entry.key);
    return key ? readNewBoxDraft(user?.id ?? null, key) : null;
  });
  const [step, setStep] = useState<NewBoxStep>(restored?.step === 'email' && user?.email && !user.isGuest ? 'participants' : restored?.step ?? 'name');
  const [draft, setDraft] = useState<NewBoxDraft>(() => {
    if (restored) return { ...restored.draft, theme: canUsePremiumThemes && restored.previewTheme ? restored.previewTheme : restored.draft.theme };
    const next = emptyDraft(
      t(`newBox.nameDefaults.${defaultName}`, { year, defaultValue: '', fallbackLng: false }),
      defaultCashCurrency(),
    );
    const preset = search.get('name');
    if (preset && ['friends', 'office', 'family'].includes(preset)) {
      const name = t(`newBox.nameDefaults.${preset}`, {
        year,
        defaultValue: '',
        fallbackLng: false,
      }).trim();
      if (name) next.name = name;
    }
    return next;
  });
  const [previewTheme, setPreviewTheme] = useState(restored?.previewTheme ?? draft.theme);
  const organizerNameEdited = useRef(Boolean(restored));
  useEffect(() => {
    const name = user?.username?.trim();
    if (!organizerNameEdited.current && name && name.length <= 64 && !isPlaceholderOrganizerUsername(name)) {
      setDraft((current) => ({ ...current, organizerName: name }));
    }
  }, [user?.username]);

  const [editingThemeFromReview, setEditingThemeFromReview] = useState(restored?.editingThemeFromReview ?? false);
  const [questionnaireBlocked, setQuestionnaireBlocked] = useState(true);
  const questionnaireDefaults = useRef<string | null>(restored?.questionnaireDefaults ?? null);
  const collectionChoices = (next: NewBoxDraft) => JSON.stringify([next.exchange, next.useWish]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [namesProblem, setNamesProblem] = useState<NamesProblem | null>(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileUploadRef = useRef<FileUploadHandle>(null);

  useEffect(() => {
    if (resumeKey && !restored) return;
    saveNewBoxDraft({ draft, previewTheme, step, editingThemeFromReview, questionnaireDefaults: questionnaireDefaults.current, userId: user?.id ?? null });
  }, [draft, previewTheme, step, editingThemeFromReview, user?.id, resumeKey, restored]);

  useEffect(() => {
    // Once Continue has applied the game theme, recover that appearance too.
    if (!restored || ['name', 'organizer', 'picture'].includes(restored.step)) return;
    void setSeason(restored.draft.theme, { persist: false }).catch(() => setError(t('newBox.saveFailed')));
  }, [restored, setSeason, t]);

  useEffect(() => {
    // Checkout may have granted features to this already-created draft game.
    if (!restored?.draft.key) return;
    let cancelled = false;
    void santaClient.getBoxInfo({ key: restored.draft.key }).then((result) => {
      if (cancelled || !result.ok || !result.data?.box) return;
      const box = result.data.box;
      setDraft((current) => ({ ...current, features: box.features, isPremium: box.isPremium === true }));
    });
    return () => { cancelled = true; };
  }, [restored]);

  useEffect(() => {
    if (resumeKey && !restored) navigate(boxPath(resumeKey), { replace: true });
    const onStorage = (event: StorageEvent) => {
      if (draft.key && event.key === newBoxDraftKey(draft.key) && event.newValue === null) {
        navigate(boxPath(draft.key), { replace: true });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [resumeKey, restored, draft.key, navigate]);

  const askEmail = !user || user.isGuest || !user.email;
  const currencyOptions = useMemo(
    () =>
      cashCurrencyCodes().map((code) => ({
        value: code,
        label: currencyOptionLabel(code, t(`currency.${code}`)),
      })),
    [t],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  useEffect(() => {
    if (resumeKey || restored?.draft.key) return;
    let cancelled = false;
    void santaClient.generateBoxKey().then((res) => {
      if (cancelled || !res.ok || !res.data) {
        return;
      }
      setDraft((current) => ({ ...current, key: res.data as string }));
    });
    return () => {
      cancelled = true;
    };
  }, [restored, resumeKey]);

  async function goNext(next = draft) {
    if (busy) return;
    if (step === 'picture' && PREMIUM_THEMES.includes(next.theme) && !canUsePremiumThemes) return;
    setError(null);
    if (step === 'cost' && next.budgetPreset === 'custom' &&
        (!Number.isFinite(next.cashLimit) || !Number.isFinite(next.cashLimitMin) ||
         next.cashLimit <= 0 || next.cashLimit > 10000000 ||
         next.cashLimitMin < 0 || next.cashLimitMin > next.cashLimit)) {
      setError(t('newBox.cost.invalidRange'));
      return;
    }
    if (step === 'picture' && isSeason(next.theme)) {
      setBusy(true);
      try {
        await setSeason(next.theme, { persist: false });
        if (user && !canUsePremiumThemes && PREMIUM_THEMES.includes(previewTheme)) {
          saveDeferredTheme(next.key, user.id, previewTheme);
        } else {
          clearDeferredTheme(next.key);
        }
      } catch {
        setError(t('newBox.saveFailed'));
        return;
      } finally {
        setBusy(false);
      }
    }
    if (step === 'participants') {
      setBusy(true);
      try {
        const response = await santaClient.getQuestions({ key: next.key });
        if (!response.ok || !response.data) throw new Error('newBox.saveFailed');
        if (
          questionnaireDefaults.current === null ||
          questionnaireDefaults.current !== collectionChoices(next)
        ) {
          const [previousExchange, previousWish] = JSON.parse(
            questionnaireDefaults.current ?? '[null,null]',
          );
          const fields = response.data.map((field: CustomField) =>
            field.type === 'santa_letter' && previousWish !== next.useWish
              ? { ...field, enabled: next.useWish === true }
              : field.type === 'delivery_address' && previousExchange !== next.exchange
                ? {
                    ...field,
                    enabled: ['send', 'pickup'].includes(next.exchange ?? ''),
                    config: {
                      deliveryMode:
                        next.exchange === 'pickup' ? ('pickup' as const) : ('postal' as const),
                    },
                  }
                : field.type === 'phone' && previousExchange !== next.exchange
                  ? {
                      ...field,
                      enabled: ['send', 'pickup'].includes(next.exchange ?? ''),
                      required: ['send', 'pickup'].includes(next.exchange ?? ''),
                    }
                  : field,
          );
          const saved = await santaClient.saveQuestions({ key: next.key, fields });
          if (!saved.ok) throw new Error('newBox.saveFailed');
        }
        questionnaireDefaults.current = collectionChoices(next);
      } catch {
        setError(t('newBox.saveFailed'));
        return;
      } finally {
        setBusy(false);
      }
    }
    setDraft(next);
    if (editingThemeFromReview && step === 'picture') {
      setEditingThemeFromReview(false);
      setStep('review');
    } else setStep((current) => nextStep(current, askEmail));
  }

  function selectOption(next: NewBoxDraft) {
    if (mobile) {
      setError(null);
      setDraft(next);
    } else {
      goNext(next);
    }
  }

  function goBack() {
    if (step === 'review' && questionnaireBlocked) {
      setError(t('newBox.review.questionsHint'));
      return;
    }
    if (editingThemeFromReview) {
      setEditingThemeFromReview(false);
      setStep('review');
      return;
    }
    setError(null);
    if (step === 'name') {
      navigate('/');
      return;
    }
    setStep((current) => prevStep(current, askEmail));
  }

  const [confirmedGuestUserId, setConfirmedGuestUserId] = useState<number | null>(null);
  const existingGames = user?.profiles.find((item) => item.id === user.id)?.boxes ?? [];
  const needsGuestChoice = Boolean(user?.isGuest && existingGames.length > 0 &&
    !existingGames.some((box) => box.key === draft.key) && confirmedGuestUserId !== user.id);
  useEffect(() => {
    if (!needsGuestChoice || !user) return;
    showDialog({
      title: t(user.isPremium ? 'newBox.guestProfileChoice.premiumTitle' : 'newBox.guestProfileChoice.title'),
      testId: 'new-box-guest-profile-choice',
      onCancel: () => navigate('/'),
      children: <Stack gap={4} className="new-box-guest-profile-choice">
        <Typography as="p" role="body/base">{t(user.isPremium ? 'newBox.guestProfileChoice.premiumBody' : 'newBox.guestProfileChoice.body')}</Typography>
        {user.isPremium && <Typography as="p" role="body/strong">{t('newBox.guestProfileChoice.premium')}</Typography>}
        <Button testId="new-box-keep-profile" onClick={() => {
          closeDialog();
          navigate('/account#permanent-account');
        }}>{t('newBox.guestProfileChoice.permanent')}</Button>
        <Button variant="secondary" testId="new-box-new-profile" onClick={() => {
          setConfirmedGuestUserId(user.id);
          closeDialog();
        }}>{t('newBox.guestProfileChoice.continue')}</Button>
      </Stack>,
    });
    return () => closeDialog();
  }, [needsGuestChoice, user, navigate, t]);

  const savingOrganizer = useRef(false);
  async function saveOrganizer() {
    if (savingOrganizer.current || busy || needsGuestChoice) return;
    savingOrganizer.current = true;
    setBusy(true);
    setError(null);
    try {
      let key = draft.key;
      if (!key) {
        const generated = await santaClient.generateBoxKey();
        if (!generated.ok || !generated.data) throw new Error('key unavailable');
        key = generated.data;
      }
      // Establish the organizer session and draft before showing Premium themes.
      const result = await santaClient.createBox(
        toCreateInput(
          { ...draft, key },
          {
            isCreated: false,
            ...(questionnaireDefaults.current !== null
              ? {
                  usePost: undefined,
                  usePickupPoint: undefined,
                  useWish: undefined,
                  isPhoneRequired: undefined,
                }
              : {}),
            adminUsername: draft.organizerName.trim(),
          },
        ),
      );
      if (!result.ok) {
        setError(t(result.error?.message || 'newBox.saveFailed'));
        return;
      }
      setDraft((current) => ({ ...current, key, features: result.data?.box?.features, isPremium: result.data?.box?.isPremium === true }));
      await dispatch(loadSession());
      setStep(nextStep('organizer', askEmail));
    } catch {
      setError(t('newBox.saveFailed'));
    } finally {
      savingOrganizer.current = false;
      setBusy(false);
    }
  }

  const finishing = useRef(false);
  async function finish() {
    if (
      draft.organizerName.trim().length < 2 ||
      busy ||
      finishing.current ||
      questionnaireBlocked ||
      namesProblem
    )
      return;
    finishing.current = true;
    try {
      setBusy(true);
      setError(null);
      let key = draft.key;
      if (!key) {
        const keyRes = await santaClient.generateBoxKey();
        if (!keyRes.ok || !keyRes.data) {
          setError(t('newBox.saveFailed'));
          return;
        }
        key = keyRes.data as string;
        setDraft((current) => ({ ...current, key }));
      }
      const extras = extrasWithoutOrganizer(draft.participantNames, draft.organizerName);
      const res = await santaClient.createBox(
        toCreateInput(
          { ...draft, key },
          {
            isCreated: true,
            usePost: undefined,
            usePickupPoint: undefined,
            useWish: undefined,
            isPhoneRequired: undefined,
            adminUsername: draft.organizerName.trim(),
            participantNames: extras,
            createAdminCard: draft.participates === true,
            email: draft.email.trim() || undefined,
          },
        ),
      );
      if (!res.ok) {
        setError(t(res.error?.message || 'newBox.saveFailed'));
        return;
      }
      trackUmamiEvent('box_created');
      if (res.data?.organizerAccess) {
        storeOrganizerAccess(key, res.data.organizerAccess);
      }
      await santaClient.updateAdminUsername({
        key,
        username: draft.organizerName.trim(),
      });
      await dispatch(loadSession());
      if (user) completeDeferredTheme(key, user.id);
      clearNewBoxDraft(key);
      navigate(`${boxOrganizerPath(key)}?tab=users&people=invite`);
    } catch {
      setError(t('newBox.saveFailed'));
    } finally {
      finishing.current = false;
      setBusy(false);
    }
  }

  const wizard = visibleNewBoxSteps(askEmail);
  const stepNumber = Math.max(1, wizard.indexOf(step) + 1);
  const copyKey = newBoxStepCopyKey(step);

  const choiceReady =
    step === 'exchange'
      ? draft.exchange !== null
      : step === 'cost'
        ? draft.budgetPreset !== null &&
          (draft.budgetPreset !== 'custom' ||
            (Number.isFinite(draft.cashLimit) && Number.isFinite(draft.cashLimitMin) &&
              draft.cashLimit > 0 && draft.cashLimit <= 10000000 &&
              draft.cashLimitMin >= 0 &&
              draft.cashLimitMin <= draft.cashLimit))
        : step === 'letter'
          ? draft.useWish !== null
          : step === 'picture'
            ? (canUsePremiumThemes || !PREMIUM_THEMES.includes(draft.theme)) &&
              Boolean(draft.picture && (draft.picture !== 'custom_image' || draft.logo))
            : false;

  const choiceAction =
    step === 'picture' && !canUsePremiumThemes && PREMIUM_THEMES.includes(previewTheme) ? (
      <Stack gap={2} wide>
        <Button variant={colorTheme === 'dark' ? 'premium-light' : 'premium-dark'}
          icon={<CrownIcon />} fullWidth disabled={busy || uploadingPicture}
          onClick={() => startPremiumPurchase('creation_theme_bottom', 'themes', 'creation')}
          testId="new-box-premium-unlock">
          <span className="premium-upsell__button-label premium-upsell__button-label--creation"><span><PremiumOfferView placement="creation_theme_bottom" feature="themes" context="creation">{isTieredVariant(premiumOffer.variant) ? premiumFeatureAction('themes', i18n.language, premiumOffer.symbolPrice) : t('newBox.unlockPremiumPriced', { price: premiumOffer.symbolPrice })}</PremiumOfferView></span></span>
        </Button>
        <Button variant="ghost" size="small"
          disabled={!choiceReady || busy || uploadingPicture}
          onClick={() => void goNext()} testId="new-box-continue">
          {t('newBox.decideThemeLater')}
        </Button>
      </Stack>
    ) : mobile && ['exchange', 'cost', 'letter', 'picture'].includes(step) ? (
      <Button
        variant="primary"
        disabled={!choiceReady || busy || uploadingPicture}
        onClick={() => goNext()}
        testId="new-box-continue"
      >
        {step === 'picture'
          ? t('newBox.continueWithTheme', { theme: t(`newBox.appearance.${draft.theme}`) })
          : t('newBox.continue')}
      </Button>
    ) : null;

  const shell = (title: string, subtitle: string, children: ReactNode, action?: ReactNode) => (
    <CreationShell
      showHeader={false}
      mobileFocused={mobile}
      stepKey={step}
      decorated={step !== 'picture'}
      footer={!mobile}
      afterForm={!mobile ? <InlineAds /> : undefined}
      heading={t(`newBox.${copyKey}.header`)}
      title={title}
      subtitle={subtitle}
      step={stepNumber}
      steps={wizard.length}
      onBack={goBack}
      action={choiceAction || action}
      error={<HintError error={error} testId="new-box-error" />}
      testId="new-box"
    >
      {children}
    </CreationShell>
  );

  if (step === 'name') {
    return shell(
      t('newBox.name.title'),
      t('newBox.name.subtitle'),
      <>
        <Input
          value={draft.name}
          maxLength={BOX_NAME_MAX_LENGTH}
          onValueChange={(name) => setDraft((current) => ({ ...current, name }))}
          onClear={() => setDraft((current) => ({ ...current, name: '' }))}
          label={t('newBox.name.label')}
          labelEnd={
            <Typography as="span" role="meta/helper">
              {draft.name.length} / {BOX_NAME_MAX_LENGTH}
            </Typography>
          }
          testId="new-box-name"
        />
        <NamePresetPicker
          value={draft.name}
          year={year}
          onSelect={(name) => setDraft((current) => ({ ...current, name }))}
          testId="new-box-presets"
        />
      </>,
      <Button
        variant="primary"
        disabled={!draft.name.trim() || busy}
        loading={busy}
        onClick={() => void goNext()}
        testId="new-box-continue"
      >
        {t('newBox.continue')}
      </Button>,
    );
  }

  if (step === 'exchange') {
    const pick = (method: ExchangeMethod) => {
      if (busy) {
        return;
      }
      const next = {
        ...draft,
        exchange: method,
        usePost: method === 'send',
        isPhoneRequired: method === 'send' || method === 'pickup',
      };
      selectOption(next);
    };
    return shell(
      t('newBox.exchange.title'),
      t('newBox.exchange.subtitle'),
      <ChoicePanel plain>
        {(['meet', ...(!isDotCom() ? ['pickup'] : []), 'send', 'later'] as ExchangeMethod[]).map(
          (method) => {
            const { tone, Icon } = EXCHANGE_CHOICE_ICONS[method];
            return (
              <Choice
                key={method}
                name="exchange"
                value={method}
                checked={draft.exchange === method}
                onCheckedChange={() => pick(method)}
                icon={
                  <ChoiceIcon tone={tone}>
                    <Icon />
                  </ChoiceIcon>
                }
                title={t(`newBox.exchange.${method}.title`)}
                description={t(`newBox.exchange.${method}.body`)}
                testId={`new-box-exchange-${method}`}
              />
            );
          },
        )}
      </ChoicePanel>,
    );
  }

  if (step === 'cost') {
    const currency = (draft.cashLimitCurrency || defaultCashCurrency()) as CashCurrency;
    const amounts = getBudgetPresetAmounts(currency);
    const symbol = getCurrencySymbol(currency);
    const pick = (preset: BudgetPreset) => {
      if (busy) {
        return;
      }
      const next = {
        ...draft,
        cashLimitCurrency: currency,
        budgetPreset: preset,
        useCashLimit: preset !== 'none',
        cashLimitMin:
          preset === 'low'
            ? 0
            : preset === 'mid'
              ? amounts.midMin
              : draft.cashLimitMin || amounts.midMin,
        cashLimit:
          preset === 'low'
            ? amounts.low
            : preset === 'mid'
              ? amounts.midMax
              : draft.cashLimit || amounts.midMax,
      };
      if (preset === 'custom') {
        setDraft(next);
        return;
      }
      selectOption(next);
    };
    return shell(
      t('newBox.cost.title'),
      t('newBox.cost.subtitle'),
      <>
        <Select
          value={currency}
          options={currencyOptions}
          onValueChange={(value) => {
            const cashLimitCurrency = value as CashCurrency;
            const nextAmounts = getBudgetPresetAmounts(cashLimitCurrency);
            setDraft((current) => {
              const preset = current.budgetPreset;
              const presetFields =
                preset === 'low'
                  ? { cashLimitMin: 0, cashLimit: nextAmounts.low }
                  : preset === 'mid'
                    ? { cashLimitMin: nextAmounts.midMin, cashLimit: nextAmounts.midMax }
                    : {};
              return { ...current, cashLimitCurrency, ...presetFields };
            });
          }}
          label={t('newBox.cost.currency')}
          testId="new-box-currency"
        />
        <ChoicePanel plain>
          {BUDGET_PRESETS.map((preset) => (
            <Choice
              key={preset}
              name="budget"
              value={preset}
              checked={draft.budgetPreset === preset}
              onCheckedChange={() => pick(preset)}
              title={
                preset === 'low'
                  ? t('newBox.cost.low.title', { amount: formatMoney(amounts.low), symbol })
                  : preset === 'mid'
                    ? t('newBox.cost.mid.title', {
                        min: formatMoney(amounts.midMin),
                        max: formatMoney(amounts.midMax),
                        symbol,
                      })
                    : t(`newBox.cost.${preset}.title`)
              }
              description={t(`newBox.cost.${preset}.body`)}
              testId={`new-box-budget-${preset}`}
            />
          ))}
        </ChoicePanel>
        {draft.budgetPreset === 'custom' ? (
          <Cluster gap={3} wide wrap={false} className="new-box-budget-range">
            <Input
              value={draft.cashLimitMin ? String(draft.cashLimitMin) : ''}
              onValueChange={(value) =>
                setDraft((current) => ({ ...current, cashLimitMin: Number(value) || 0 }))
              }
              numeric
              label={t('newBox.cost.min')}
              suffix={symbol}
              grow
              testId="new-box-budget-min"
            />
            <Input
              value={draft.cashLimit ? String(draft.cashLimit) : ''}
              onValueChange={(value) =>
                setDraft((current) => ({ ...current, cashLimit: Number(value) || 0 }))
              }
              numeric
              label={t('newBox.cost.max')}
              suffix={symbol}
              grow
              error={draft.cashLimit > 0 && draft.cashLimitMin > draft.cashLimit
                ? t('newBox.cost.minExceedsMax') : undefined}
              testId="new-box-budget-max"
            />
          </Cluster>
        ) : null}
      </>,
      draft.budgetPreset === 'custom' ? (
        <Button
          disabled={!choiceReady || busy}
          variant="primary"
          loading={busy}
          onClick={() => void goNext()}
          testId="new-box-continue"
        >
          {t('newBox.continue')}
        </Button>
      ) : undefined,
    );
  }

  if (step === 'letter') {
    return shell(
      t('newBox.letter.title'),
      t('newBox.letter.subtitle'),
      <ChoicePanel plain>
        <Choice
          name="letter"
          value="yes"
          checked={draft.useWish === true}
          onCheckedChange={() => {
            if (!busy) {
              selectOption({ ...draft, useWish: true });
            }
          }}
          title={t('newBox.letter.yes.title')}
          description={t('newBox.letter.yes.body')}
          testId="new-box-letter-yes"
        />
        <Choice
          name="letter"
          value="no"
          checked={draft.useWish === false}
          onCheckedChange={() => {
            if (!busy) {
              selectOption({ ...draft, useWish: false });
            }
          }}
          title={t('newBox.letter.no.title')}
          description={t('newBox.letter.no.body')}
          testId="new-box-letter-no"
        />
      </ChoicePanel>,
    );
  }

  if (step === 'picture') {
    const pickPicture = (picture: string) => {
      if (busy || uploadingPicture) {
        return;
      }
      if (picture === 'custom_image') {
        fileUploadRef.current?.open();
        return;
      }
      trackThemeIconSelected(draft.theme, 'creation');
      setDraft({ ...draft, picture, logo: null });
    };

    return shell(
      t('newBox.picture.title'),
      t('newBox.picture.subtitle'),
      <>
        <AppearancePicker
          themeOrder={themeOrder}
          onThumbnailClick={(theme, placement) => trackCreationFirstThumbnail(theme, draft.key, homeThemeAtCreation, placement, firstHomeThemeAtCreation)}
          theme={draft.theme}
          initialBrowsedTheme={previewTheme}
          onBrowseTheme={setPreviewTheme}
          isPremium={canUsePremiumThemes}
          name={draft.name}
          picture={draft.picture}
          logo={draft.logo}
          disabled={busy || uploadingPicture}
          onThemeChange={(theme) => setDraft((current) => ({ ...current, theme }))}
        >
          <PicturePicker
            value={draft.picture}
            logo={draft.logo}
            loadingPicture={uploadingPicture ? 'custom_image' : null}
            disabled={busy || uploadingPicture}
            onValueChange={pickPicture}
            testId="new-box-picture"
          />
        </AppearancePicker>
        <FileUpload
          ref={fileUploadRef}
          kind="image"
          maxSize={IMAGE_UPLOAD_MAX_SIZE}
          onChange={(state, file) => {
            if (state === 'selected') {
              setUploadingPicture(true);
              setError(null);
              return;
            }
            setUploadingPicture(false);
            if (state === 'uploaded' && file) {
              trackThemeIconSelected(draft.theme, 'creation');
              setDraft((current) => ({
                ...current,
                picture: 'custom_image',
                logo: file.url,
              }));
              return;
            }
            if (state === 'error') {
              setError('upload.failed');
            }
          }}
        />
      </>,
      <Button
        variant="primary"
        disabled={!choiceReady || busy || uploadingPicture}
        loading={busy}
        onClick={() => void goNext()}
        testId="new-box-continue"
      >
        {t('newBox.continueWithTheme', { theme: t(`newBox.appearance.${draft.theme}`) })}
      </Button>,
    );
  }

  if (step === 'organizer') {
    const ready =
      draft.participates !== null &&
      draft.organizerName.trim().length >= 2 &&
      draft.organizerName.trim().length <= 64 &&
      !isPlaceholderOrganizerUsername(draft.organizerName.trim());
    return shell(
      t('newBox.organizer.title'),
      t('newBox.organizer.combinedSubtitle'),
      <Stack gap={4} wide>
        <Input
          value={draft.organizerName}
          onValueChange={(organizerName) => {
            organizerNameEdited.current = true;
            setDraft((current) => ({ ...current, organizerName }));
          }}
          label={t('newBox.organizer.label')}
          testId="new-box-organizer"
        />
        <ChoicePanel plain title={t('newBox.participate.title')}>
          {([true, false] as const).map((participates) => (
            <Choice
              key={String(participates)}
              name="play"
              value={participates ? 'yes' : 'no'}
              checked={draft.participates === participates}
              onCheckedChange={() => {
                if (!busy) setDraft((current) => ({ ...current, participates }));
              }}
              title={t(`newBox.participate.${participates ? 'yes' : 'no'}.title`)}
              description={t(`newBox.participate.${participates ? 'yes' : 'no'}.body`)}
              testId={`new-box-play-${participates ? 'yes' : 'no'}`}
            />
          ))}
        </ChoicePanel>
      </Stack>,
      <Button
        variant="primary"
        disabled={!ready}
        loading={busy}
        onClick={(event) => {
          // Cancel native submission before the next step renders its action.
          event.preventDefault();
          void saveOrganizer();
        }}
        testId="new-box-continue"
      >
        {t('newBox.continue')}
      </Button>,
    );
  }

  if (step === 'email') {
    return (
      <NewBoxEmailStep
        email={draft.email}
        shell={shell}
        onEmailChange={(email) => setDraft((current) => ({ ...current, email }))}
        onComplete={(email) => goNext({ ...draft, email })}
      />
    );
  }

  const extras = extrasWithoutOrganizer(draft.participantNames, draft.organizerName);
  if (step === 'review') {
    const valid =
      draft.name.trim().length > 0 &&
      draft.name.trim().length <= 40 &&
      draft.organizerName.trim().length >= 2 &&
      draft.organizerName.trim().length <= 64 &&
      !isPlaceholderOrganizerUsername(draft.organizerName.trim()) &&
      !namesProblem &&
      (canUsePremiumThemes || !PREMIUM_THEMES.includes(draft.theme)) &&
      (draft.budgetPreset === 'none' ||
        (Number.isFinite(draft.cashLimit) &&
          Number.isFinite(draft.cashLimitMin) &&
          draft.cashLimit > 0 &&
          draft.cashLimit <= 10000000 &&
          draft.cashLimitMin >= 0 &&
          draft.cashLimitMin <= draft.cashLimit));
    return shell(
      t('newBox.review.title'),
      t('newBox.review.subtitle'),
      <NewBoxReview
        draft={{ ...draft, participantNames: extras }}
        onChange={setDraft}
        onNamesProblem={setNamesProblem}
        onQuestionnaireBlocked={setQuestionnaireBlocked}
        onEditTheme={() => {
          if (questionnaireBlocked) {
            setError(t('newBox.review.questionsHint'));
            return;
          }
          setEditingThemeFromReview(true);
          setStep('picture');
        }}
      />,
      <Button
        variant="primary"
        disabled={!valid || questionnaireBlocked || busy}
        loading={busy}
        onClick={() => void finish()}
        testId="new-box-create"
      >
        {t('newBox.create')}
      </Button>,
    );
  }
  return shell(
    t('newBox.people.title'),
    t(draft.participates ? 'newBox.people.subtitlePlay' : 'newBox.people.subtitle'),
    <NamesInput
      names={extras}
      onNamesChange={(participantNames) =>
        setDraft((current) => ({ ...current, participantNames }))
      }
      onProblem={setNamesProblem}
      label={t('newBox.people.label')}
      placeholder={t('newBox.people.placeholder')}
      hint={t('newBox.people.hint')}
      error={namesProblem ? t(`randomizer.problem.${namesProblem}`) : undefined}
      removeLabel={(name) => t('randomizer.names.remove', { name })}
      testId="new-box-people"
    />,
    <Button
      variant="primary"
      disabled={draft.organizerName.trim().length < 2 || busy}
      loading={busy}
      onClick={() => void goNext()}
      testId="new-box-continue"
    >
      {t('newBox.continue')}
    </Button>,
  );
}
